import { useT } from "@/i18n/LanguageContext";
import { loc } from "@/lib/logicApi";

/* Stoic Logic page chrome strings (EN / NL). Course content itself comes
   localised from the backend; these are the fixed UI labels around it. */
const UI = {
  en: {
    discipline: "Academy of Practical Wisdom · Discipline I",
    phase: "Phase", lesson: "Lesson",
    skill: "Skill in training:",
    situation: "The Situation",
    coreInsight: "Core Insight",
    essential: "Essential Understanding",
    academicDepth: "Academic Depth",
    sourceStudy: "Source Study",
    livingLibrary: "Living Library →",
    exercise: "Examination Exercise",
    practiceBeyond: "Practice Beyond the Academy",
    reflection: "Reflection · saved to your work",
    openRecord: "Open the Record of Judgment →",
    writeFreely: "Write freely. Your first response is material, not a mistake.",
    distinguished: "Distinguished clearly.",
    enterHall: "Enter the Hall",
    beginFirst: "Begin the First Practice",
    sixPhases: "The Six Phases",
    practiceContinues: "Your Practice Continues",
    lastActive: "Last active:",
    resumeLesson: "Resume lesson",
    recordOfJudgment: "Record of Judgment",
    pathOfPractice: "Path of Practice",
    theTerms: "The Terms",
    finalAssessment: "Phase VI · Final Assessment",
    chamberName: "The Chamber of Judgment",
    chamberOpens: "Opens when the six phases have been walked.",
    coreAbility: "Core ability:",
    academyCrumb: "Academy",
    depthLabels: ["Academic depth", "Scholarly depth", "Plain language"],
    practiceStates: { Accepted: "Accepted", Applied: "Applied", Reflected: "Reflected" },
    options: {},
    status: { none: "Not Entered", active: "In Practice", ready: "Ready to Continue", renew: "Requires Further Practice", done: "Completed" },
    glossaryTitle: "The Terms of the Discipline",
    glossaryIntro: "Eighteen terms carry the whole architecture of Stoic Logic. Each is given in its original form, in plain language, and in academic depth. Inside the lessons these terms are clickable wherever they appear.",
    academic: "Academic.", inCourse: "In Stoic Logic.", related: "Related:", lessons: "Lessons",
    goDeeper: "Go deeper", less: "Less", allTerms: "All terms", close: "Close",
    practiceTitle: "Practice Beyond the Academy", practiceCrumb: "Practice",
    notRecorded: "Not yet recorded",
    practisedTimes: (n, d) => `Practised ${n} time${n === 1 ? "" : "s"} in the last 30 days · last on ${d}`,
    openTheRecord: "Open the Record",
    recordThis: "Record this practice",
    practiceNotePh: "One or two lines: what happened when you practised this",
    enter: "Enter",
    auditTitle: "The Monthly Self Audit",
    auditNone: "No audit recorded yet.", auditLast: "Last audit:", auditFour: "Four questions, answered in writing, once a month.",
    auditSit: "Sit for the audit", auditEnter: "Enter the audit", auditPrev: "Previous audits",
    auditQs: [
      "Which impressions gained assent too quickly this month?",
      "Which thinking pattern returned most often?",
      "Which ability is weakest in live situations?",
      "What do I want to recognise earlier next month?",
    ],
    patternsTitle: "Recurring Patterns",
    patternsIntro: "Name the thought patterns that keep returning: the standing assumptions, the favourite fallacy, the word that does quiet damage. A pattern named is a pattern you can see arriving.",
    patternPh: "For example: silence from others reads as rejection",
    nameIt: "Name it", remove: "Remove",
    pathTitle: "Path of Practice", pathCrumb: "Path",
    pathIntro: "Fifteen abilities make up the discipline of clear judgment. The levels below name what has actually been exercised: seen, understood, practised, applied, reflected, and, where you have shown it in a real situation, demonstrated. Nothing here is a reward for clicking. Each level is a reading of your own recorded practice.",
    levels: { "Not Yet Encountered": "Not Yet Encountered", Seen: "Seen", Understood: "Understood", Practised: "Practised", Applied: "Applied", Reflected: "Reflected", Demonstrated: "Demonstrated" },
    beginning: "Beginning practice", advanced: "Advanced practice", mastery: "Mastery.", exerciseLbl: "Exercise.",
    demoHint: "Demonstrated is not granted by clicking. Describe one concrete, real situation in which you exercised this ability, and what it changed.",
    submitDemo: "Submit demonstration", yourDemo: "Your demonstration",
    resetBtn: "Reset my Logic progress",
    resetWarn: "This erases lesson progress, reflections, and practice records on this device. Certain?",
    resetYes: "Yes, reset", resetKeep: "Keep it",
    recordIntro1: "This is not a diary. It is an instrument: each entry is one complete examination of one real situation, from the first impression to the lived outcome.",
    recordIntro2: "What it trains has old names: prosochē, the watchful attention that catches the impression arriving; synkatathesis, the assent you give or withhold; and prohairesis, the faculty of choice that no event can command without your consent.",
    recordGroups: { "The Event": "The Event", "The Division of Control": "The Division of Control", "The Examination": "The Examination", "The Action": "The Action", "Afterwards": "Afterwards" },
    laterNote: "These three are filled in after the action has been lived. An entry is complete only when they are.",
    whyPh: "Why the evidence permits this answer",
    saving: "Saving", saveChanges: "Save changes", enterRecord: "Enter into the Record", cancel: "Cancel",
    beginNew: "Begin a new examination",
    recordEmpty: "The Record is empty. It begins with the first situation you bring before it.",
    complete: "Complete", outcomePending: "Outcome pending", edit: "Edit", completeEntry: "Complete the entry",
    untitled: "Untitled examination",
    chamberLockedIntro: "One complex human case waits here: testimonies that disagree, information that is missing, emotional pressure, and real consequences. You will demonstrate the entire discipline, from first impression to provisional judgment, chosen action, and the evidence that would change your mind. No score is given. You will be read, and answered, as one examiner to another.",
    lessonsWalked: (w, tt) => `Lessons walked: ${w} of ${tt}`,
    chamberLockedNote: "The Chamber opens when the six phases have been walked. Continue your practice.",
    returnHall: "Return to the Hall",
    pressure: "The Pressure",
    resumeNote: "An examination is in progress on this device. Continuing will resume where you left it.",
    resumeExam: "Resume the examination", enterUnhurried: "Enter when you are ready to be unhurried",
    movement: (a, b) => `Movement ${a} of ${b}`,
    reading: "The examiner is reading your record.",
    readingSub: "This is a full reading, not a score. It may take a minute.",
    examinersReading: "The Examiner's Reading", oneExaminer: "One examiner to another",
    recognition: "Recognition of Practised Capacity",
    presentExam: "Present the examination", continueBtn: "Continue",
    chamberWhy: "Your reasoning, briefly, for the claims you withheld or rejected",
    certainty: "Your degree of certainty in this judgment",
    certaintyPh: "What sets it at this level, and no higher",
    vAnswerEvery: "Answer every statement before continuing.",
    vWhy: "Say briefly why, for at least the claims you withheld or rejected.",
    vComplete: "The Chamber reads only complete examinations. Every field asks for an answer.",
    vCertainty: "State your degree of certainty.",
    vCertaintyWhy: "Say what sets your certainty at that level.",
  },
  nl: {
    discipline: "Academie voor Praktische Wijsheid · Discipline I",
    phase: "Fase", lesson: "Les",
    skill: "Vaardigheid in training:",
    situation: "De situatie",
    coreInsight: "Kerninzicht",
    essential: "Essentieel begrip",
    academicDepth: "Academische diepgang",
    sourceStudy: "Bronnenstudie",
    livingLibrary: "Levende Bibliotheek →",
    exercise: "Onderzoeksoefening",
    practiceBeyond: "Praktijk buiten de Academie",
    reflection: "Reflectie · bewaard in je werk",
    openRecord: "Open het Record of Judgment →",
    writeFreely: "Schrijf vrijuit. Je eerste reactie is materiaal, geen fout.",
    distinguished: "Helder onderscheiden.",
    enterHall: "Betreed de Zaal",
    beginFirst: "Begin de eerste oefening",
    sixPhases: "De zes fasen",
    practiceContinues: "Je oefening gaat verder",
    lastActive: "Laatst actief:",
    resumeLesson: "Hervat les",
    recordOfJudgment: "Record of Judgment",
    pathOfPractice: "Pad van Oefening",
    theTerms: "De begrippen",
    finalAssessment: "Fase VI · Eindbeoordeling",
    chamberName: "De Chamber of Judgment",
    chamberOpens: "Opent wanneer de zes fasen zijn doorlopen.",
    coreAbility: "Kernvaardigheid:",
    academyCrumb: "Academie",
    depthLabels: ["Academische diepgang", "Wetenschappelijke diepgang", "Gewone taal"],
    practiceStates: { Accepted: "Aanvaard", Applied: "Toegepast", Reflected: "Gereflecteerd" },
    options: {
      fact: "feit", added: "toegevoegd", assent: "instemmen", withhold: "opschorten", reject: "verwerpen",
      supports: "ondersteunt", undermines: "ondermijnt", irrelevant: "irrelevant",
      valid: "geldig", invalid: "ongeldig",
      wisdom: "wijsheid", justice: "rechtvaardigheid", courage: "moed", temperance: "gematigdheid",
      "false dilemma": "vals dilemma", "personal attack": "persoonlijke aanval",
      "appeal to emotion": "beroep op emotie", contradiction: "tegenspraak",
      low: "laag", moderate: "gemiddeld", high: "hoog",
    },
    status: { none: "Niet betreden", active: "In oefening", ready: "Klaar om verder te gaan", renew: "Vraagt verdere oefening", done: "Voltooid" },
    glossaryTitle: "De begrippen van de discipline",
    glossaryIntro: "Achttien begrippen dragen de hele architectuur van de Stoïsche Logica. Elk wordt gegeven in zijn oorspronkelijke vorm, in gewone taal en in academische diepgang. In de lessen zijn deze begrippen overal aanklikbaar waar ze verschijnen.",
    academic: "Academisch.", inCourse: "In de Stoïsche Logica.", related: "Verwant:", lessons: "Lessen",
    goDeeper: "Ga dieper", less: "Minder", allTerms: "Alle begrippen", close: "Sluiten",
    practiceTitle: "Praktijk buiten de Academie", practiceCrumb: "Praktijk",
    notRecorded: "Nog niet vastgelegd",
    practisedTimes: (n, d) => `${n} keer geoefend in de laatste 30 dagen · laatst op ${d}`,
    openTheRecord: "Open het Record",
    recordThis: "Leg deze oefening vast",
    practiceNotePh: "Een of twee regels: wat gebeurde er toen je dit oefende",
    enter: "Vastleggen",
    auditTitle: "De maandelijkse zelfaudit",
    auditNone: "Nog geen audit vastgelegd.", auditLast: "Laatste audit:", auditFour: "Vier vragen, schriftelijk beantwoord, eens per maand.",
    auditSit: "Ga zitten voor de audit", auditEnter: "Leg de audit vast", auditPrev: "Eerdere audits",
    auditQs: [
      "Welke indrukken kregen deze maand te snel instemming?",
      "Welk denkpatroon keerde het vaakst terug?",
      "Welke vaardigheid is het zwakst in levensechte situaties?",
      "Wat wil ik volgende maand eerder herkennen?",
    ],
    patternsTitle: "Terugkerende patronen",
    patternsIntro: "Benoem de denkpatronen die blijven terugkeren: de vaste aannames, de favoriete drogreden, het woord dat stille schade aanricht. Een benoemd patroon is een patroon dat je kunt zien aankomen.",
    patternPh: "Bijvoorbeeld: stilte van anderen lees ik als afwijzing",
    nameIt: "Benoem het", remove: "Verwijder",
    pathTitle: "Pad van Oefening", pathCrumb: "Pad",
    pathIntro: "Vijftien vaardigheden vormen samen de discipline van het heldere oordeel. De niveaus hieronder benoemen wat werkelijk is geoefend: gezien, begrepen, geoefend, toegepast, gereflecteerd, en, waar je het in een echte situatie hebt getoond, gedemonstreerd. Niets hier is een beloning voor klikken. Elk niveau is een lezing van je eigen vastgelegde oefening.",
    levels: { "Not Yet Encountered": "Nog niet ontmoet", Seen: "Gezien", Understood: "Begrepen", Practised: "Geoefend", Applied: "Toegepast", Reflected: "Gereflecteerd", Demonstrated: "Gedemonstreerd" },
    beginning: "Beginnende oefening", advanced: "Gevorderde oefening", mastery: "Meesterschap.", exerciseLbl: "Oefening.",
    demoHint: "Gedemonstreerd wordt niet toegekend door te klikken. Beschrijf één concrete, echte situatie waarin je deze vaardigheid uitoefende, en wat het veranderde.",
    submitDemo: "Dien demonstratie in", yourDemo: "Jouw demonstratie",
    resetBtn: "Wis mijn Logica-voortgang",
    resetWarn: "Dit wist lesvoortgang, reflecties en praktijkgegevens op dit apparaat. Zeker weten?",
    resetYes: "Ja, wissen", resetKeep: "Behouden",
    recordIntro1: "Dit is geen dagboek. Het is een instrument: elke invoer is één volledig onderzoek van één echte situatie, van de eerste indruk tot de geleefde uitkomst.",
    recordIntro2: "Wat het traint draagt oude namen: prosochē, de waakzame aandacht die de indruk ziet aankomen; synkatathesis, de instemming die je geeft of opschort; en prohairesis, het keuzevermogen dat geen gebeurtenis kan bevelen zonder jouw toestemming.",
    recordGroups: { "The Event": "De gebeurtenis", "The Division of Control": "De verdeling van controle", "The Examination": "Het onderzoek", "The Action": "De handeling", "Afterwards": "Achteraf" },
    laterNote: "Deze drie worden ingevuld nadat de handeling is geleefd. Een invoer is pas compleet wanneer dat gebeurd is.",
    whyPh: "Waarom het bewijs dit antwoord toelaat",
    saving: "Opslaan", saveChanges: "Wijzigingen opslaan", enterRecord: "Voer in het Record in", cancel: "Annuleren",
    beginNew: "Begin een nieuw onderzoek",
    recordEmpty: "Het Record is leeg. Het begint met de eerste situatie die je eraan voorlegt.",
    complete: "Compleet", outcomePending: "Uitkomst volgt", edit: "Bewerken", completeEntry: "Maak de invoer compleet",
    untitled: "Naamloos onderzoek",
    chamberLockedIntro: "Hier wacht één complexe menselijke casus: getuigenissen die elkaar tegenspreken, informatie die ontbreekt, emotionele druk en echte gevolgen. Je demonstreert de hele discipline, van eerste indruk tot voorlopig oordeel, gekozen handeling en het bewijs dat je van gedachten zou doen veranderen. Er wordt geen score gegeven. Je wordt gelezen, en beantwoord, als de ene onderzoeker tegenover de andere.",
    lessonsWalked: (w, tt) => `Doorlopen lessen: ${w} van ${tt}`,
    chamberLockedNote: "De Chamber opent wanneer de zes fasen zijn doorlopen. Ga verder met je oefening.",
    returnHall: "Keer terug naar de Zaal",
    pressure: "De druk",
    resumeNote: "Er is een onderzoek gaande op dit apparaat. Doorgaan hervat waar je gebleven was.",
    resumeExam: "Hervat het onderzoek", enterUnhurried: "Treed binnen wanneer je klaar bent om ongehaast te zijn",
    movement: (a, b) => `Beweging ${a} van ${b}`,
    reading: "De onderzoeker leest je verslag.",
    readingSub: "Dit is een volledige lezing, geen score. Het kan een minuut duren.",
    examinersReading: "De lezing van de onderzoeker", oneExaminer: "De ene onderzoeker tegenover de andere",
    recognition: "Erkenning van geoefend vermogen",
    presentExam: "Leg het onderzoek voor", continueBtn: "Ga verder",
    chamberWhy: "Je redenering, kort, voor de beweringen die je opschortte of verwierp",
    certainty: "Je mate van zekerheid over dit oordeel",
    certaintyPh: "Wat het op dit niveau zet, en niet hoger",
    vAnswerEvery: "Beantwoord elke bewering voordat je verdergaat.",
    vWhy: "Zeg kort waarom, minstens voor de beweringen die je opschortte of verwierp.",
    vComplete: "De Chamber leest alleen volledige onderzoeken. Elk veld vraagt om een antwoord.",
    vCertainty: "Geef je mate van zekerheid aan.",
    vCertaintyWhy: "Zeg wat je zekerheid op dat niveau zet.",
  },
};

export function useLogicUi() {
  const { lang, t } = useT();
  const ui = UI[lang] || UI.en;
  const L = (v) => loc(v, lang);
  const opt = (o) => ui.options[o] || o;
  return { ui, lang, L, t, opt };
}
